import './header.css';
import React, {Component} from "react";
import {UserDto} from "../model/functionModels";
import UserDetailsService from "../services/userDetailsService";
import UserDisplay from "./userDisplay";
import LocalStorageService from "../services/localStorageService";
import {withRouter} from "./withRouter";
import RedirectionService from "../services/redirectionService";
import {NavLink} from "react-router-dom";
import LanguageSelector from "./languageSelector";
import ThemeToggle from "./themeToggle";
import i18nService from "../../i18n/i18nService";
import themeService from "../../services/themeService";

interface HeaderState {
    user?: UserDto,
    mobileMenuOpen: boolean,
    locale: string,
}

class Header extends Component<{}, HeaderState> {

    private unsubscribe?: () => void;

    constructor(props: {}) {
        super(props, {})
        this.state = {
            mobileMenuOpen: false,
            locale: i18nService.getLocale(),
        };
        this.toggleMobileMenu = this.toggleMobileMenu.bind(this);
    }

    toggleMobileMenu() {
        this.setState({
            mobileMenuOpen: !this.state.mobileMenuOpen
        });
    }

    componentDidMount() {
        UserDetailsService.getOwnUserDetails(async newUser => {
                this.setState({
                    user: newUser
                });
                // Sync locale from user if available
                if (newUser.locale) {
                    i18nService.setLocale(newUser.locale as any, true);
                }
                // Sync theme from user if available
                if (newUser.theme) {
                    themeService.setTheme(newUser.theme as 'light' | 'dark', false);
                } else {
                    themeService.syncFromUser();
                }
            },
            async failure => {
                // we do nothing on failure cause we are present even when logged in
                this.setState({
                    user: undefined
                });
            },
            async redirection => {
                // we do nothing on failure cause we are present even when logged in
                this.setState({
                    user: undefined
                });
            });
        
        // Subscribe to locale changes
        this.unsubscribe = i18nService.subscribe(() => {
            this.setState({ locale: i18nService.getLocale() });
        });
        
        // Close mobile menu when clicking outside
        document.addEventListener('click', this.handleClickOutside);
    }

    componentDidUpdate() {
        // Prevent body scroll when mobile menu is open
        if (this.state.mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
    }

    componentWillUnmount() {
        if (this.unsubscribe) {
            this.unsubscribe();
        }
        document.removeEventListener('click', this.handleClickOutside);
        // Restore body scroll when component unmounts
        document.body.style.overflow = '';
    }

    handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (this.state.mobileMenuOpen && 
            !target.closest('.Header-MobileMenu') && 
            !target.closest('.Header-Hamburger')) {
            this.setState({ mobileMenuOpen: false });
        }
    }


    render() {
        return (
            <div className="Header-Wrapper">
                <NavLink to="/dashboard" end className="Header-Title" onClick={() => { if (this.state.mobileMenuOpen) this.setState({mobileMenuOpen: false}); }}>Expense Tracker</NavLink>
                <button className={`Header-Hamburger ${this.state.mobileMenuOpen ? 'Header-Hamburger-Open' : ''} ${!this.state.user ? 'Header-Hamburger-Hidden' : ''}`} onClick={this.toggleMobileMenu} aria-label="Toggle menu">
                    <span className="Header-Hamburger-Line"></span>
                    <span className="Header-Hamburger-Line"></span>
                    <span className="Header-Hamburger-Line"></span>
                </button>
                {this.state.user && (
                    <>
                        <div className={`Header-MobileMenu ${this.state.mobileMenuOpen ? 'Header-MobileMenu-Open' : ''}`}>
                            {this.state.mobileMenuOpen && (
                                <button className="Header-CloseButton" onClick={() => { this.setState({mobileMenuOpen: false}); }} aria-label="Close menu">
                                    <span className="Header-CloseButton-Line"></span>
                                    <span className="Header-CloseButton-Line"></span>
                                </button>
                            )}
                            <nav className="Header-Navigation">
                                <NavLink to="/dashboard" end className={({ isActive }) => isActive ? "Header-NavLink Header-NavLink-Active" : "Header-NavLink"} onClick={() => { this.setState({mobileMenuOpen: false}); }}>{i18nService.t('nav.dashboard')}</NavLink>
                                <NavLink to="/friends" className={({ isActive }) => isActive ? "Header-NavLink Header-NavLink-Active" : "Header-NavLink"} onClick={() => { this.setState({mobileMenuOpen: false}); }}>{i18nService.t('nav.friends')}</NavLink>
                                <NavLink to="/profile" className={({ isActive }) => isActive ? "Header-NavLink Header-NavLink-Active" : "Header-NavLink"} onClick={() => { this.setState({mobileMenuOpen: false}); }}>{i18nService.t('nav.profile')}</NavLink>
                                <NavLink to="/history" className={({ isActive }) => isActive ? "Header-NavLink Header-NavLink-Active" : "Header-NavLink"} onClick={() => { this.setState({mobileMenuOpen: false}); }}>{i18nService.t('nav.history')}</NavLink>
                            </nav>
                            <div className="Header-UserActions">
                                <div className="Header-LanguageSelector-Mobile">
                                    <LanguageSelector />
                                </div>
                                <div className="Header-ThemeToggle-Mobile">
                                    <ThemeToggle />
                                </div>
                                <NavLink to="/profile" className="Header-User-Mobile" onClick={() => { this.setState({mobileMenuOpen: false}); }}><UserDisplay user={this.state.user} showName={true}/></NavLink>
                                <button className="Header-Signout-Mobile" onClick={() => {
                                    LocalStorageService.signOut();
                                    // @ts-ignore
                                    this.props.navigate(RedirectionService.redirectToLogin());
                                }}>{i18nService.t('auth.logout')}</button>
                            </div>
                        </div>
                        <div className="Header-DesktopActions">
                            <nav className="Header-Navigation">
                                <NavLink to="/dashboard" end className={({ isActive }) => isActive ? "Header-NavLink Header-NavLink-Active" : "Header-NavLink"}>{i18nService.t('nav.dashboard')}</NavLink>
                                <NavLink to="/friends" className={({ isActive }) => isActive ? "Header-NavLink Header-NavLink-Active" : "Header-NavLink"}>{i18nService.t('nav.friends')}</NavLink>
                                <NavLink to="/profile" className={({ isActive }) => isActive ? "Header-NavLink Header-NavLink-Active" : "Header-NavLink"}>{i18nService.t('nav.profile')}</NavLink>
                                <NavLink to="/history" className={({ isActive }) => isActive ? "Header-NavLink Header-NavLink-Active" : "Header-NavLink"}>{i18nService.t('nav.history')}</NavLink>
                            </nav>
                            <div className="Header-UserActions">
                                <div className="Header-LanguageSelector-Desktop">
                                    <LanguageSelector />
                                </div>
                                <div className="Header-ThemeToggle-Desktop">
                                    <ThemeToggle />
                                </div>
                                <NavLink to="/profile" className="Header-User"><UserDisplay user={this.state.user} showName={true}/></NavLink>
                                <button className="Header-Signout-Desktop" onClick={() => {
                                    LocalStorageService.signOut();
                                    // @ts-ignore
                                    this.props.navigate(RedirectionService.redirectToLogin());
                                }}>{i18nService.t('auth.logout')}</button>
                            </div>
                        </div>
                    </>
                )}
            </div>);
    }
}

export default withRouter(Header);